<template>
  <el-form ref="form" :model="questionsModel" label-position="top">
    <h1 class="mb0">Spiritual Gifts Assessment</h1>

    <p class="quote mt1">
      “Now about spiritual gifts, brothers, I do not want you to be ignorant.” <br>
      1 Corinthians 12:1 (NIV)
    </p>

    <p>
      A spiritual gift is a special ability given by the Holy Spirit to serve others and strengthen the Body of Christ.
    </p>

    <h2>Ways to Discover Our Spiritual Gifts:</h2>

    <el-row :gutter="20">
      <el-col :md="12">
        <p>
          <b>Experiment</b> <br>
          It’s easier to discover your gift through ministry than to discover your ministry through your gift.
        </p>
      </el-col>
      <el-col :md="12">
        <p>
          <b>Read and Study</b> <br>
          There are a number of books that talk about spiritual gifts.
        </p>
      </el-col>
    </el-row>

    <el-row :gutter="20">
      <el-col :md="12">
        <p>
          <b>Take assessments</b> <br>
          Such as the S.H.A.P.E. assessment.
        </p>
      </el-col>
      <el-col :md="12">
        <p>
          <b>Ask others for input</b> <br>
          Others will often see gifts in us that we can’t see ourselves.
        </p>
      </el-col>
    </el-row>
    <br>

    <h2>Spiritual Gifts:</h2>

    <el-alert title="Important" type="warning" :closable="false">
      <br>
      Answer according to who you are, not who you would like to be or think you ought to be.
      How true are these statements for you? What has been your experience? To what degree do these statements
      reflect your usual tendencies?
    </el-alert>

    <p>&nbsp;</p>

    <el-steps ref="stepsComponent" :active="currentStep" finish-status="success">
      <el-step v-for="step, idx in steps" :key="step" :title="step" @click.native="onStepClick(idx)" />
    </el-steps>

    <p>&nbsp;</p>

    <el-form-item
        class="question"
        v-for="question, idx in stepQuestions"
        :key="getMasterIndex(idx)"
        :label="(getMasterIndex(idx) + 1) + '. ' + question.question"
        :prop="getMasterIndex(idx).toString()"
        :rules="[{ required: true, message: 'Required' }]">
      <el-radio-group
          :value="question.answer"
          @input="setAnswer(getMasterIndex(idx), arguments[0])"
          size="medium">
        <el-radio-button label="3">Consistently, definitely true</el-radio-button>
        <el-radio-button label="2">Most of the time, usually true</el-radio-button>
        <el-radio-button label="1">Some of the time, once in a while</el-radio-button>
        <el-radio-button label="0">Not at all, never</el-radio-button>
      </el-radio-group>
    </el-form-item>

    <ui-form-actions>
      <el-button v-if="currentStep === 0" @click="goBack">Back</el-button>
      <el-button v-if="currentStep > 0" @click="stepBack">Back</el-button>
      <el-button v-if="currentStep === steps.length - 1" type="primary" @click="goForward">Continue</el-button>
      <el-button v-else type="primary" @click="stepForward">Continue</el-button>
    </ui-form-actions>
  </el-form>
</template>

<script>
  import findIndex from 'lodash/findIndex';
  import fromPairs from 'lodash/fromPairs';
  import slice from 'lodash/slice';
  import stepMixin from '../mixins/step';
  import validation from '../mixins/validation';

  export default {
    mixins: [stepMixin, validation],
    name: 'Gifts',
    methods: {
      onStepClick(idx) {
        this.validate().then(() => {
          this.currentStep = idx;
        }).catch(() => {
        });
      },
      stepBack() {
        this.currentStep -= 1;
        this.scrollToSteps();
      },
      stepForward() {
        this.validate().then(() => {
          this.currentStep += 1;
          this.scrollToSteps();
        }).catch(() => {
        });
      },
      scrollToSteps() {
        this.$nextTick(() => {
          window.scrollTo(
            0,
            (this.$refs.stepsComponent.$el.getBoundingClientRect().top + document.documentElement.scrollTop) - 40,
          );
        });
      },
      setAnswer(index, answer) {
        this.$store.commit('setGiftAnswer', { index, answer });
      },
      getMasterIndex(indexInStepChunk) {
        return indexInStepChunk + (this.currentStep * this.questionsPerStep);
      },
      goBack() {
        this.$router.back();
      },
      goForward() {
        this.validate().then(() => {
          try {
            this.jumpToFirstUnansweredQuestion();
          } catch (err) {
            this.$router.push({ path: '/heart' });
          }
        }).catch(() => {
        });
      },
      onGoToStep(payload) {
        this.validate().then(() => {
          try {
            this.jumpToFirstUnansweredQuestion();
          } catch (err) {
            this.$_goToStep(payload);
          }
        }).catch(() => {
        });
      },
      jumpToFirstUnansweredQuestion() {
        if (this.firstUnansweredQuestionIdx === -1) {
          throw new Error('No unanswered questions');
        }

        this.currentStep = Math.floor(this.firstUnansweredQuestionIdx / 20);
        this.$nextTick(() => {
          this.validate();
        });
      },
    },
    computed: {
      questionsModel() {
        return fromPairs(this.questions.map((question, questionIdx) => [questionIdx, question.answer]));
      },
      questions() {
        return this.$store.state.gifts;
      },
      questionsPerStep() {
        return 20;
      },
      numberOfSteps() {
        return Math.ceil(this.questions.length / this.questionsPerStep);
      },
      stepQuestions() {
        const startingIndex = this.currentStep * this.questionsPerStep;
        return slice(this.questions, startingIndex, startingIndex + this.questionsPerStep);
      },
      steps() {
        const steps = [];
        for (let step = 0; step < this.numberOfSteps; step += 1) {
          const first = (step * this.questionsPerStep) + 1;
          let last = (first + this.questionsPerStep) - 1;

          if (last > this.questions.length) {
            last = this.questions.length;
          }

          steps.push(`${first} - ${last}`);
        }
        return steps;
      },
      firstUnansweredQuestionIdx() {
        return findIndex(this.questions, question => question.answer === null);
      },
    },
    data() {
      return {
        currentStep: 0,
      };
    },
  };
</script>

<style lang="scss">
  @import "~styles/vars";

  .question {
    //border-bottom: 1px solid $--border-color-base;
    padding-bottom: 1rem;

    .el-form-item__label {
      font-weight: 600;
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/Gifts.vue